import React, { useState, useCallback } from 'react';
import * as ScenarioAPI from '../../../services/ScenarioServices';
import * as SituationAPI from '../../../services/SituationServices';
import styles from './styles/ViewScenarioTitlePage.module.css';
import { useScenario } from '../../../context/ScenarioContext';
import { transformIframes } from '../../../utils/utils';
import { findDraftStartId } from '../../../utils/draftPlayAdapter';
import { TUTORIAL_TARGETS, tutorialAttr } from '../../../tutorials';

const ViewScenarioTitlePage = ({
    scenario,
    canvas,
    setCurrentSituationId,
    preview,
    clearScenarioKeys,
    assignmentId,
}) => {
    const { startOrGetLog } = useScenario();
    const [isStarting, setIsStarting] = useState(false);

    const handleStart = useCallback(async () => {
        if (isStarting || !scenario?._id) return;
        setIsStarting(true);

        try {
            // Preview plays the DRAFT, so the start has to come from the draft
            // graph. `getStartingSituation` reads the published Situation
            // documents, which for an unpublished scenario do not exist yet —
            // previewing one would simply do nothing.
            const startSituation = preview
                ? { _id: findDraftStartId(canvas?.nodes) }
                : await SituationAPI.getStartingSituation(scenario._id);

            if (!startSituation?._id) {
                console.error('No starting situation found for scenario:', scenario._id);
                setIsStarting(false);
                return;
            }

            let activeLog = null;
            if (scenario.isLogged && !preview) {
                activeLog = await startOrGetLog(scenario._id, startSituation._id, assignmentId);
            }

            const actualSituationId = activeLog?.currentSituation || startSituation._id;

            // A player always starts over with no keys. A preview keeps whatever
            // the author chose to keep when they restarted or exited, so they can
            // test a later part of the scenario without replaying the route there.
            if (!preview && clearScenarioKeys) {
                await clearScenarioKeys();
            }

            // Setting the position triggers ViewScenario's progress effect for
            // the live teacher view — starting never waits on the socket.
            setCurrentSituationId(actualSituationId);
            if (!preview) {
                await ScenarioAPI.incrementViews(scenario._id);
            }
            setIsStarting(false);
        } catch (error) {
            console.error('Error during scenario start process:', error);
            setIsStarting(false);
        }
    }, [
        isStarting,
        scenario,
        canvas,
        preview,
        assignmentId,
        startOrGetLog,
        setCurrentSituationId,
        clearScenarioKeys,
    ]);



    return (
        <div className={styles.container}>
            {scenario.image && (
                <div className={styles.imageContainer}>
                    <img src={scenario.image} alt={scenario.title || 'Scenario Image'} className={styles.image} />
                </div>
            )}

            <div className={styles.title}>
                {scenario.title}
            </div>

            {scenario.description && (
                <div className={styles.description}>
                    <div dangerouslySetInnerHTML={{ __html: transformIframes(scenario.description) }} />
                </div>
            )}

            <button
                onClick={handleStart}
                className={styles.button}
                disabled={isStarting}
                {...tutorialAttr(TUTORIAL_TARGETS.PREVIEW_START)}
            >
                {isStarting ? 'Starting...' : 'Start'}
            </button>

        </div>
    );
};

export default React.memo(ViewScenarioTitlePage);
